import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { SelectionPanel } from '../SelectionPanel/SelectionPanel';
import { SelectionTrigger } from '../SelectionTrigger/SelectionTrigger';
import { type ServerSelectProps } from '../types/server-selection.types';

import { useServerSelectLogic } from './useServerSelectLogic';

/**
 * One value out of a table — the picker W6 mandates wherever options come from
 * the server. Never a `<Select>` with a large `pageSize`.
 *
 * Rows are RADIOS rather than a `cmdk` listbox, and that is the same
 * accessibility argument `MultiSelect` has always carried: `cmdk` owns
 * `aria-selected` for its roving highlight, so the one attribute meaning "this
 * value is chosen" would instead mean "the cursor is here" — and until P10.2a
 * this control signalled its own selection with nothing but an `aria-hidden`
 * check glyph. Radix's `RadioGroup` gives the roving focus, the arrow keys and
 * the direction awareness back, natively.
 *
 * A `combobox`, unlike its multi siblings: this trigger displays the chosen
 * VALUE, which is what the role promises.
 */
export const ServerSelect = forwardRef<HTMLButtonElement, ServerSelectProps>(function ServerSelect(
  {
    id,
    className,
    size,
    placeholder,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
    'aria-labelledby': labelledBy,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const panelId = useId();
  const logic = useServerSelectLogic({ ...rest, isDisabled, isReadOnly });

  return (
    // `min-w-0`, for the reason `Combobox` states: a truncating summary needs a
    // parent that may shrink below its content, or a long value pushes the
    // whole control out of its cell.
    <div className={cn('flex min-w-0 flex-col', className)}>
      <SelectionTrigger
        ref={ref}
        role="combobox"
        id={id}
        panelId={panelId}
        isOpen={logic.isOpen}
        size={size}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        placeholder={placeholder ?? t('serverSelect.placeholder')}
        summary={logic.selectedOption?.label ?? ''}
        onBlur={onBlur}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        clear={
          logic.isClearVisible ? { label: t('multiSelect.clear'), onClear: logic.clear } : undefined
        }
        onOpenChange={logic.onOpenChange}
      >
        <SelectionPanel mode="single" {...logic.panel} />
      </SelectionTrigger>
    </div>
  );
});
